import { ColorBaseToken, getColorBaseTokenValue } from "../base/colors";
import { COLOR_SEMANTIC_TOKENS, ColorSemanticToken } from "../tokens";

export type ColorThemeName = "light" | "dark";

type ColorThemeDefinition = Record<ColorSemanticToken, ColorBaseToken>;

export const colorThemes: Record<ColorThemeName, ColorThemeDefinition> = {
  light: {
    "color.ui.bg.default": "color.neutral.000",
    "color.ui.bg.surface": "color.neutral.100",
    "color.ui.bg.subtle": "color.neutral.100",
    "color.ui.bg.hover": "color.neutral.100",
    "color.ui.text.primary": "color.neutral.900",
    "color.ui.text.secondary": "color.neutral.500",
    "color.ui.text.disabled": "color.neutral.300",
    "color.ui.text.inverse": "color.neutral.000",
    "color.ui.icon.primary": "color.neutral.700",
    "color.ui.icon.secondary": "color.neutral.500",
    "color.ui.action.primary": "color.primary.500",
    "color.ui.action.secondary": "color.secondary.500",
    "color.ui.border.default": "color.neutral.300",
    "color.ui.border.secondary": "color.neutral.300",
    "color.ui.border.subtle": "color.neutral.100",
    "color.ui.border.hover": "color.neutral.500",
    "color.state.hover": "color.neutral.100",
    "color.state.active": "color.primary.500",
    "color.state.focus": "color.primary.300",
    "color.state.disabled": "color.neutral.300"
  },
  dark: {
    "color.ui.bg.default": "color.neutral.700",
    "color.ui.bg.surface": "color.primary.900",
    "color.ui.bg.subtle": "color.primary.700",
    "color.ui.bg.hover": "color.primary.700",
    "color.ui.text.primary": "color.neutral.000",
    "color.ui.text.secondary": "color.neutral.300",
    "color.ui.text.disabled": "color.neutral.500",
    "color.ui.text.inverse": "color.neutral.700",
    "color.ui.icon.primary": "color.neutral.100",
    "color.ui.icon.secondary": "color.neutral.300",
    "color.ui.action.primary": "color.primary.300",
    "color.ui.action.secondary": "color.secondary.300",
    "color.ui.border.default": "color.primary.700",
    "color.ui.border.secondary": "color.primary.700",
    "color.ui.border.subtle": "color.primary.900",
    "color.ui.border.hover": "color.primary.500",
    "color.state.hover": "color.primary.300",
    "color.state.active": "color.primary.100",
    "color.state.focus": "color.secondary.300",
    "color.state.disabled": "color.neutral.500"
  }
} as const;

export type ColorTheme = ColorThemeDefinition;

export type ResolvedColorTheme = Record<ColorSemanticToken, string>;

export const resolveColorTheme = (themeName: ColorThemeName): ResolvedColorTheme => {
  const theme = colorThemes[themeName];
  const resolved = {} as ResolvedColorTheme;
  COLOR_SEMANTIC_TOKENS.forEach((token) => {
    const reference = theme[token];
    resolved[token] = getColorBaseTokenValue(reference);
  });
  return resolved;
};

export const getColorTokenValue = (
  themeName: ColorThemeName,
  token: ColorSemanticToken
): string => {
  const theme = colorThemes[themeName];
  const reference = theme[token];
  return getColorBaseTokenValue(reference);
};

export const colorThemeNames: ColorThemeName[] = ["light", "dark"];

export const getColorThemeReference = (
  themeName: ColorThemeName,
  token: ColorSemanticToken
): ColorBaseToken => colorThemes[themeName][token];
